import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { colors } from 'styles/colors';
import { useUserData } from 'hooks/useUserData';
import { useArtistProfiles } from 'hooks/swrRequests/useArtistProfiles';
import { usePublishingView } from 'hooks/swrRequests/usePublishingView';
import { usePublishingActions } from 'hooks/swrRequests/usePublishingActions';
import { usePublishingAdminArtists } from 'hooks/swrRequests/usePublishingAdmin';
import type { IdentityDetails, PublishingRosterArtist, PublishingTrack } from 'types/publishing';
import { useTranslation } from 'react-i18next';
import { buckets, selectedIsrcs, tileKind } from './derive-ui';
import { PublishingHeader } from './components/PublishingHeader';
import { ArtistPicker } from './components/ArtistPicker';
import { ArtistProfileButtons } from './components/ArtistProfileButtons';
import { OfferTile } from './components/OfferTile';
import { Scope, ScopeTiles } from './components/ScopeTiles';
import { TrackLists } from './components/TrackLists';
import { HelpDialog } from './components/HelpDialog';
import { IdentityDialog } from './components/IdentityDialog';
import { DoneDialog } from './components/DoneDialog';
import { TrackDialog } from './components/TrackDialog';
import { Toast } from './components/Toast';
import { ui } from './ui';

type DialogState =
  | { kind: 'help'; isrcs?: string[] }
  | { kind: 'identity'; rejectOnly: boolean }
  | { kind: 'track'; track: PublishingTrack }
  | { kind: 'done' }
  | null;

const PublishingSection: React.FC = () => {
  const { t } = useTranslation('publishing');
  const history = useHistory();
  const location = useLocation();
  const { permissionsData } = useUserData();
  const admin = !!permissionsData?.admin;

  const { profiles, isLoading: loadingProfiles } = useArtistProfiles();
  const { artists: roster, isLoading: loadingRoster } = usePublishingAdminArtists(admin);
  const ownedArtists = useMemo<PublishingRosterArtist[]>(
    () =>
      (profiles || [])
        .filter((p) => p.spotifyId)
        .map((p) => ({
          spotifyArtistId: p.spotifyId as string,
          artistName: p.artistName || p.spotifyId || '',
          artistImage: p.artistImage,
        })),
    [profiles]
  );
  const artists = admin ? roster : ownedArtists;
  const ownedIds = useMemo(() => ownedArtists.map((a) => a.spotifyArtistId), [ownedArtists]);

  const requested = new URLSearchParams(location.search).get('artist');
  const [artistId, setArtistId] = useState<string | null>(null);
  useEffect(() => {
    if (artistId && artists.some((a) => a.spotifyArtistId === artistId)) return;
    const preferred =
      requested && artists.some((a) => a.spotifyArtistId === requested) ? requested : null;
    setArtistId(preferred || artists[0]?.spotifyArtistId || null);
  }, [artists, requested, artistId]);

  // Admins may open any artist read-only; writes only on an owned profile (AC-18).
  const readOnly = admin && !(artistId && ownedIds.includes(artistId));
  const { view, isLoading, notFound, error, refetch } = usePublishingView(artistId);
  const { createBatch, exclude, restore, answerIdentity, isBusy } = usePublishingActions(
    artistId,
    refetch
  );

  const [scope, setScope] = useState<Scope | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [deselected, setDeselected] = useState<Set<string>>(new Set());
  const [toastMsg, setToastMsg] = useState('');
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setDeselected(new Set());
    setDialog(null);
    setScope(null);
  }, [artistId]);

  const toast = useCallback((msg: string) => {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 2800);
  }, []);

  const b = useMemo(() => buckets(view?.tracks || []), [view]);
  const blockedIdentity = !!view && view.artist.identity !== 'usable';
  const selected = useMemo(() => selectedIsrcs(b.missing, deselected), [b, deselected]);
  const nSel = selected.length;

  const onCheck = (isrc: string, on: boolean) =>
    setDeselected((s) => {
      const next = new Set(s);
      if (on) next.delete(isrc);
      else next.add(isrc);
      return next;
    });

  const markNotMine = async (isrc: string) => {
    const r = await exclude(isrc);
    if (r.ok) toast(t('toast_hid'));
    else toast(r.message);
  };

  const restoreTracks = async (isrcs: string[]) => {
    const r = await restore(isrcs);
    if (r.ok) {
      setDeselected((s) => {
        const next = new Set(s);
        isrcs.forEach((i) => next.delete(i));
        return next;
      });
      toast(t('toast_restored'));
    } else toast(r.message);
  };

  const restoreHidden = () =>
    restoreTracks(
      (view?.tracks || []).filter((x) => x.notMine && x.isrc).map((x) => x.isrc as string)
    );

  const helpIsrcs = dialog?.kind === 'help' && dialog.isrcs ? dialog.isrcs : selected;
  const helpTitles = useMemo(() => {
    const byIsrc = new Map((view?.tracks || []).map((x) => [x.isrc, x.title]));
    return helpIsrcs.map((isrc) => byIsrc.get(isrc) || isrc);
  }, [helpIsrcs, view]);

  const sendMissing = async () => {
    if (!helpIsrcs.length) return;
    const r = await createBatch(helpIsrcs);
    if (r.ok) {
      setDialog({ kind: 'done' });
    } else if (r.rejected?.length) {
      // 409: the view was re-fetched; reopen with the rejected tracks unchecked
      setDeselected((s) => {
        const next = new Set(s);
        r.rejected!.forEach((x) => next.add(x.isrc));
        return next;
      });
    } else {
      setDialog(null);
      toast(r.message);
    }
  };

  const sendIdentity = async (matches: boolean, details?: IdentityDetails) => {
    const r = await answerIdentity(matches ? 'thats_me' : 'not_me', details);
    if (r.ok) {
      setDialog({ kind: 'done' });
    } else {
      setDialog(null);
      toast(r.message);
    }
  };

  const onPick = (id: string) => {
    setArtistId(id);
    if (requested) history.replace({ pathname: location.pathname, search: `?artist=${id}` });
  };

  if (loadingProfiles || (admin && loadingRoster)) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: colors.main.purple }} />
      </Box>
    );
  }

  const showPicker = artists.length > 1 || admin;
  const content = 'box-border w-full px-7 pt-[22px] pb-14';

  if (!artistId) {
    return (
      <div className={content}>
        <p className={`m-0 ${ui.muted}`}>
          {admin ? t('empty_no_roster') : t('empty_no_spotify_id')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {showPicker && (
        <div className="box-border max-w-[880px] px-7 pt-[22px]">
          {admin ? (
            <ArtistPicker artists={artists} value={artistId} onChange={onPick} admin />
          ) : (
            <ArtistProfileButtons artists={artists} value={artistId} onChange={onPick} />
          )}
        </div>
      )}
      <div className={content}>
        {isLoading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress sx={{ color: colors.main.purple }} />
          </Box>
        )}
        {!isLoading && (notFound || !view) && (
          <p className={`m-0 ${ui.muted}`}>{error ? error.message : t('empty_not_checked')}</p>
        )}

        {view && (
          <>
            <PublishingHeader
              artist={view.artist}
              readOnly={readOnly}
              onNotMe={() => setDialog({ kind: 'identity', rejectOnly: true })}
            />
            <OfferTile
              kind={tileKind(view, b)}
              b={b}
              readOnly={readOnly}
              answered={view.answers.length > 0}
              onConfirmId={() => setDialog({ kind: 'identity', rejectOnly: false })}
            />
            <ScopeTiles
              blocked={b.blocked.length}
              missing={b.missing.length}
              ready={b.ready.length}
              done={b.done.length}
              scope={blockedIdentity ? null : scope}
              blockedIdentity={blockedIdentity}
              onChange={setScope}
            />
            <TrackLists
              b={b}
              legalName={view.artist.legalName}
              scope={blockedIdentity ? null : scope}
              selected={selected}
              nSel={nSel}
              readOnly={readOnly}
              onOpen={(track) => setDialog({ kind: 'track', track })}
              onCheck={onCheck}
              onNotMine={markNotMine}
              onRestore={restoreHidden}
              onHelp={() => nSel && setDialog({ kind: 'help' })}
            />
          </>
        )}
      </div>

      {view && (
        <>
          <HelpDialog
            open={dialog?.kind === 'help'}
            titles={helpTitles}
            busy={isBusy}
            onClose={() => setDialog(null)}
            onConfirm={sendMissing}
          />
          <IdentityDialog
            open={dialog?.kind === 'identity'}
            rejectOnly={dialog?.kind === 'identity' && dialog.rejectOnly}
            candidate={
              view.artist.candidateName
                ? { name: view.artist.candidateName, pro: view.artist.pro, ipi: view.artist.ipi }
                : null
            }
            readOnly={readOnly}
            busy={isBusy}
            onClose={() => setDialog(null)}
            onConfirm={() => sendIdentity(true)}
            onWrong={(details) => sendIdentity(false, details)}
          />
          {dialog?.kind === 'track' && (
            <TrackDialog
              track={dialog.track}
              artist={view.artist}
              canNotMine={
                dialog.track.state === 'not_listed' &&
                !dialog.track.batchId &&
                !dialog.track.notMine
              }
              canRegister={
                dialog.track.state === 'not_listed' &&
                !dialog.track.batchId &&
                !dialog.track.notMine
              }
              canRestore={dialog.track.notMine}
              readOnly={readOnly}
              onRegister={() => {
                const isrc = dialog.track.isrc;
                if (isrc) setDialog({ kind: 'help', isrcs: [isrc] });
              }}
              onClose={() => setDialog(null)}
              onNotMine={() => {
                const isrc = dialog.track.isrc;
                setDialog(null);
                if (isrc) markNotMine(isrc);
              }}
              onRestore={() => {
                const isrc = dialog.track.isrc;
                setDialog(null);
                if (isrc) restoreTracks([isrc]);
              }}
            />
          )}
        </>
      )}

      <DoneDialog open={dialog?.kind === 'done'} onClose={() => setDialog(null)} />
      <Toast message={toastMsg} />
    </div>
  );
};

export default PublishingSection;
